import React, { useEffect, useRef, useState } from "react";
import { useGameStateContext } from "./GameStateProvider";
import { useAudioContext } from "./AudioProvider";
import { useTutorialContext } from "./TutorialProvider";
import useGameLoop from "./hooks/useGameLoop";
import useCustomerManagement from "./hooks/useCustomerManagement";
import useLegendaryUnlocks from "./hooks/useLegendaryUnlocks";
import useQuestSystem from "./hooks/useQuestSystem";
import { useAiBarista } from "./hooks/useAiBarista";
import { useSeedActions } from "./hooks/useSeedActions";

// Import screen components
import IntroScreen from "./components/screens/IntroScreen";
import MenuScreen from "./components/screens/MenuScreen";
import GameScreen from "./components/GameScreen";
import ResultsScreen from "./components/screens/ResultsScreen";

// Import new GlobalUI component
import GlobalUI from "./components/GlobalUI";

import { CONSTANTS } from "./data";
import { generateAutoSeed, parseSeededData } from "./utils/seedUtils";

const GameContainer: React.FC = () => {
  const { state, dispatch } = useGameStateContext();
  const audio = useAudioContext();
  const tutorial = useTutorialContext();
  const challengeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [urlSeedProcessed, setUrlSeedProcessed] = useState(false);
  const { handleStartSeeded, handleCopyShareCode } = useSeedActions();

  // Cleanup effect for the challenge timer
  useEffect(() => {
    return () => {
      if (challengeTimeoutRef.current) {
        clearTimeout(challengeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const epoch = new Date('2024-01-01').getTime();
    const now = Date.now();
    const daysSinceEpoch = Math.floor((now - epoch) / (1000 * 60 * 60 * 24));
    
    const seasonIndex = Math.floor(daysSinceEpoch / CONSTANTS.ROTATING_SEASONS.ROTATION_DURATION_DAYS) % CONSTANTS.ROTATING_SEASONS.SEASON_ORDER.length;
    const seasonType = CONSTANTS.ROTATING_SEASONS.SEASON_ORDER[seasonIndex];
    const seasonData = CONSTANTS.ROTATING_SEASONS.DEFINITIONS[seasonType];
  
    dispatch({ type: 'SET_ACTIVE_SEASON', season: { ...seasonData, type: seasonType } });
  }, [dispatch]);

  // Check for seed in URL on mount. This is robust against race conditions with activeSeason.
  useEffect(() => {
    // Exit if there's no active season yet, or if we've already processed the URL seed.
    if (!state.activeSeason || urlSeedProcessed) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const seedFromUrl = urlParams.get('seed');

    if (seedFromUrl && seedFromUrl.trim()) {
      try {
        console.log("Attempting to parse seed from URL:", seedFromUrl);
        
        const seedData = parseSeededData(seedFromUrl.trim(), state.activeSeason.item || '');
        console.log("Parsed seed data:", seedData);
        
        if (seedData) {
          dispatch({ type: "SET_CUSTOM_SEED", seed: seedFromUrl.trim() });
          dispatch({ type: "APPLY_SEED", seedData });
          
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('seed');
          window.history.replaceState({}, '', newUrl.toString());
          
          dispatch({ 
            type: "SET_HUD_HINT", 
            hint: "Game state loaded from share link!" 
          });
          setTimeout(() => {
            dispatch({ type: "CLEAR_HUD_HINT" });
          }, 3000);
        }
      } catch (error) {
        console.error("Failed to parse seed from URL:", error);
        dispatch({ 
          type: "SET_HUD_HINT", 
          hint: "Invalid share link - starting fresh game" 
        });
        setTimeout(() => {
          dispatch({ type: "CLEAR_HUD_HINT" });
        }, 3000);
        
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('seed');
        window.history.replaceState({}, '', newUrl.toString());
      }
    }
    
    // Mark the URL seed as processed to prevent this effect from running again for the same URL load.
    setUrlSeedProcessed(true);

  }, [dispatch, state.activeSeason, urlSeedProcessed]);

  useGameLoop({ state, dispatch, audio, tutorial });
  useCustomerManagement({ state, dispatch });
  useLegendaryUnlocks({ state, dispatch });
  useQuestSystem({ state, dispatch });
  useAiBarista();

  // Event handlers for screens
  const handleStartGame = () => {
    dispatch({ type: "SET_SCREEN", screen: "menu" });
  };

  const handleStartEndless = () => {
    dispatch({
      type: "START_SESSION",
      mode: { type: "endless", startTime: Date.now(), score: 0, served: 0, tips: 0, combosDiscovered: 0 }
    });
  };

  const handleStartRush = () => {
    dispatch({ type: "START_RUSH_MODE" });
  };

  const handleMainMenu = () => {
    dispatch({ type: "SET_SCREEN", screen: "menu" });
  };

  const handlePlayAgain = () => {
    if (state.sessionMode.type === "rush") {
      handleStartRush();
    } else {
      handleStartEndless();
    }
  };

  const handleStartChallenge = (challengeId: string) => {
    const challenge = CONSTANTS.MASTER_CHEF_CHALLENGES[challengeId];
    if (!challenge || state.masterChefChallenge.active) return;

    dispatch({ 
      type: "START_CHALLENGE", 
      challengeId,
      challenge: {
        ...challenge,
        startTime: Date.now(),
        progress: 0,
        active: true
      }
    });

    dispatch({ 
      type: "SET_HUD_HINT", 
      hint: CONSTANTS.UI_MESSAGES.CHALLENGE_START.replace('{name}', challenge.name)
    });

    setTimeout(() => {
      dispatch({ type: "CLEAR_HUD_HINT" });
    }, 3000);

    // Clear any existing timeout before setting a new one
    if (challengeTimeoutRef.current) {
      clearTimeout(challengeTimeoutRef.current);
    }

    // Auto-end challenge after duration
    challengeTimeoutRef.current = setTimeout(() => {
      if (state.masterChefChallenge.active && state.masterChefChallenge.id === challengeId) {
        const currentProgress = state.masterChefChallenge.progress || 0;
        if (currentProgress >= challenge.goal) {
          // Challenge succeeded
          dispatch({ type: "COMPLETE_CHALLENGE", success: true });
          
          // Apply reward
          if (challenge.reward.type === 'coins') {
            dispatch({ type: "ADD_COINS", amount: challenge.reward.amount });
          } else if (challenge.reward.type === 'buff') {
            // Apply buff logic here if needed
          }

          dispatch({ 
            type: "SET_HUD_HINT", 
            hint: CONSTANTS.UI_MESSAGES.CHALLENGE_SUCCESS.replace('{reward}', 
              challenge.reward.type === 'coins' ? `${challenge.reward.amount} coins` : challenge.reward.description)
          });
        } else {
          // Challenge failed
          dispatch({ type: "COMPLETE_CHALLENGE", success: false });
          dispatch({ 
            type: "SET_HUD_HINT", 
            hint: CONSTANTS.UI_MESSAGES.CHALLENGE_FAILURE
          });
        }

        setTimeout(() => {
          dispatch({ type: "CLEAR_HUD_HINT" });
        }, 3000);
      }
    }, challenge.duration);
  };

  // Render appropriate screen
  const renderScreen = () => {
    switch (state.screen) {
      case "intro":
        return <IntroScreen onStartGame={handleStartGame} />;
        
      case "menu":
        return (
          <MenuScreen
            onStartEndless={handleStartEndless}
            onStartRush={handleStartRush}
            onStartSeeded={handleStartSeeded}
            onCopyShareCode={handleCopyShareCode}
            isLoading={state.isLoading}
          />
        );
        
      case "game":
        return <GameScreen />;
        
      case "results":
        return (
          <ResultsScreen
            lastRushScore={state.lastRushScore}
            onMainMenu={handleMainMenu}
            onPlayAgain={handlePlayAgain}
            isLoading={state.isLoading}
          />
        );
        
      default:
        return <IntroScreen onStartGame={handleStartGame} />;
    }
  };

  return (
    <div className="relative font-sans text-text-primary overflow-hidden h-full">
      {/* Main Screen */}
      {renderScreen()}
      
      {/* Global UI (Modals and Overlays) */}
      <GlobalUI onStartChallenge={handleStartChallenge} />
    </div>
  );
};

export default GameContainer;
